import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
  PlayIcon,
  PauseIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  usePriceListsPage,
  usePriceListSummary,
  useCreatePriceList,
  useUpdatePriceList,
  useDeletePriceList,
  useSetPriceListActive,
} from '../../hooks/usePriceLists';
import {
  PriceList,
  CreatePriceListData,
  UpdatePriceListData,
  PriceListType,
  PriceListStatus,
  PriceType,
} from '../../types/priceList';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  ConfirmDialog,
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  getApiErrorMessage,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import PriceListForm, {
  PRICE_LIST_TYPE_LABELS,
  PRICE_TYPE_LABELS,
} from '../../components/sales/PriceListForm';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_META: Record<PriceListStatus, { label: string; color: string }> = {
  [PriceListStatus.ACTIVE]: { label: 'Active', color: 'text-green-700 bg-green-50 ring-green-600/20' },
  [PriceListStatus.DRAFT]: { label: 'Draft', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20' },
  [PriceListStatus.INACTIVE]: { label: 'Inactive', color: 'text-gray-700 bg-gray-50 ring-gray-500/20' },
  [PriceListStatus.EXPIRED]: { label: 'Expired', color: 'text-red-700 bg-red-50 ring-red-600/20' },
};

const TYPE_COLORS: Record<PriceListType, string> = {
  [PriceListType.STANDARD]: 'text-blue-700 bg-blue-50 ring-blue-600/20',
  [PriceListType.CUSTOMER_SPECIFIC]: 'text-purple-700 bg-purple-50 ring-purple-600/20',
  [PriceListType.PROMOTIONAL]: 'text-orange-700 bg-orange-50 ring-orange-600/20',
  [PriceListType.QUANTITY_BASED]: 'text-teal-700 bg-teal-50 ring-teal-600/20',
};

const formatNumber = (value: number) => Number(value || 0).toLocaleString('en-IN');

const formatCurrency = (amount: number, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(Number(amount || 0));

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

// A list past its end date is expired, whatever status was stored
const getEffectiveStatus = (priceList: PriceList): PriceListStatus => {
  if (priceList.validTo) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (new Date(priceList.validTo) < today) return PriceListStatus.EXPIRED;
  }
  return priceList.status;
};

const PriceListManagement: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<PriceListType | ''>('');
  const [statusFilter, setStatusFilter] = useState<PriceListStatus | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingList, setEditingList] = useState<PriceList | null>(null);
  const [viewList, setViewList] = useState<PriceList | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PriceList | null>(null);
  const [toggleTarget, setToggleTarget] = useState<{ priceList: PriceList; activate: boolean } | null>(null);

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: listPage, isLoading, isFetching, error } = usePriceListsPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
  });
  const { data: summary, isLoading: isSummaryLoading } = usePriceListSummary();
  const createMutation = useCreatePriceList();
  const updateMutation = useUpdatePriceList();
  const deleteMutation = useDeletePriceList();
  const setActiveMutation = useSetPriceListActive();

  const priceLists = listPage?.data ?? [];
  const total = listPage?.total ?? 0;
  const totalPages = listPage?.totalPages ?? 1;

  // Step back when the current page no longer exists
  useEffect(() => {
    if (listPage && page > 1 && page > listPage.totalPages) {
      setPage(Math.max(1, listPage.totalPages));
    }
  }, [listPage, page]);

  const openCreate = () => {
    setEditingList(null);
    setIsFormOpen(true);
  };

  const openEdit = (priceList: PriceList) => {
    setEditingList(priceList);
    setIsFormOpen(true);
  };

  const openView = (priceList: PriceList) => {
    setViewList(priceList);
    setIsViewOpen(true);
  };

  const handleSubmit = async (data: CreatePriceListData | UpdatePriceListData) => {
    try {
      if (editingList) {
        await updateMutation.mutateAsync({ id: editingList.id, data });
        toast.success('Price list updated successfully');
      } else {
        await createMutation.mutateAsync(data as CreatePriceListData);
        toast.success('Price list created successfully');
        setPage(1);
      }
      setIsFormOpen(false);
    } catch (err) {
      toast.error(
        getApiErrorMessage(err, editingList ? 'Could not update the price list' : 'Could not create the price list'),
      );
      throw err;
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Price list deleted successfully');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete the price list'));
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    try {
      await setActiveMutation.mutateAsync({ id: toggleTarget.priceList.id, active: toggleTarget.activate });
      toast.success(toggleTarget.activate ? 'Price list activated' : 'Price list deactivated');
      setToggleTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not change the price list status'));
    }
  };

  const hasFilters = !!(search || typeFilter || statusFilter);

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const stats = [
    { label: 'Total Price Lists', value: summary?.total ?? 0, icon: TagIcon, tone: 'text-indigo-600 bg-indigo-50' },
    { label: 'Active', value: summary?.active ?? 0, icon: CheckCircleIcon, tone: 'text-green-600 bg-green-50' },
    { label: 'Draft', value: summary?.draft ?? 0, icon: DocumentTextIcon, tone: 'text-yellow-600 bg-yellow-50' },
    { label: 'Expired', value: summary?.expired ?? 0, icon: ClockIcon, tone: 'text-red-600 bg-red-50' },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const iconButtonClass = 'p-1.5 rounded disabled:opacity-50';

  const renderStatus = (priceList: PriceList) => {
    const status = getEffectiveStatus(priceList);
    return <span className={`${badgeClass} ${STATUS_META[status].color}`}>{STATUS_META[status].label}</span>;
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading price lists</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Price Lists</h1>
          <p className="mt-1 text-sm text-gray-600">Manage product pricing and discounts</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Price List
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">
                {isSummaryLoading ? '—' : formatNumber(stat.value)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code, name or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as PriceListType | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All types</option>
              {Object.values(PriceListType).map((type) => (
                <option key={type} value={type}>
                  {PRICE_LIST_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as PriceListStatus | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              {Object.values(PriceListStatus).map((status) => (
                <option key={status} value={status}>
                  {STATUS_META[status].label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Price lists table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Price List</th>
                <th className={thClass}>Type</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Validity</th>
                <th className={thClass}>Priority</th>
                <th className={thClass}>Prices</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-center text-sm text-gray-500" colSpan={7}>
                    Loading price lists...
                  </td>
                </tr>
              ) : priceLists.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={7}>
                    <TagIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No price lists found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Create a price list to give products special prices on sales orders.'}
                    </p>
                  </td>
                </tr>
              ) : (
                priceLists.map((priceList) => {
                  const status = getEffectiveStatus(priceList);
                  const isActive = status === PriceListStatus.ACTIVE;
                  return (
                    <tr key={priceList.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{priceList.name}</div>
                        <div>
                          <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                            {priceList.code}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className={`${badgeClass} ${TYPE_COLORS[priceList.type]}`}>
                          {PRICE_LIST_TYPE_LABELS[priceList.type]}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">{renderStatus(priceList)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        <div>{formatDate(priceList.validFrom)}</div>
                        <div className={status === PriceListStatus.EXPIRED ? 'text-xs text-red-600' : 'text-xs text-gray-500'}>
                          {priceList.validTo ? `to ${formatDate(priceList.validTo)}` : 'no end date'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 tabular-nums">
                        {priceList.priority}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 tabular-nums">
                        {priceList.items?.length || 0}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1">
                          {status !== PriceListStatus.EXPIRED && (
                            <button
                              onClick={() => setToggleTarget({ priceList, activate: !isActive })}
                              className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${
                                isActive
                                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  : 'bg-green-50 text-green-700 hover:bg-green-100'
                              }`}
                            >
                              {isActive ? (
                                <PauseIcon className="h-3.5 w-3.5 mr-1" />
                              ) : (
                                <PlayIcon className="h-3.5 w-3.5 mr-1" />
                              )}
                              {isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          )}
                          <button
                            onClick={() => openView(priceList)}
                            className={`${iconButtonClass} text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                            title="View Details"
                            aria-label="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openEdit(priceList)}
                            className={`${iconButtonClass} text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900`}
                            title="Edit Price List"
                            aria-label="Edit Price List"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(priceList)}
                            className={`${iconButtonClass} text-red-600 hover:bg-red-50 hover:text-red-900`}
                            title="Delete Price List"
                            aria-label="Delete Price List"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} price lists
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination
              className="lg:flex-1"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Create or edit */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => !(createMutation.isPending || updateMutation.isPending) && setIsFormOpen(false)}
        title={editingList ? 'Edit Price List' : 'Create Price List'}
        description={
          editingList ? editingList.code : 'Set special prices for products, for everyone or for one customer.'
        }
        size="2xl"
        className="overflow-visible"
      >
        <PriceListForm
          key={editingList?.id || 'new'}
          priceList={editingList}
          onSubmit={handleSubmit}
          onCancel={() => setIsFormOpen(false)}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Details */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Price List Details" size="xl">
        {viewList && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p>
                  <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                    {viewList.code}
                  </span>
                </p>
                <h4 className="mt-2 text-lg font-semibold text-gray-900">{viewList.name}</h4>
                <p className="mt-1 text-sm text-gray-600">{viewList.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`${badgeClass} ${TYPE_COLORS[viewList.type]}`}>
                  {PRICE_LIST_TYPE_LABELS[viewList.type]}
                </span>
                {renderStatus(viewList)}
              </div>
            </div>

            <DetailCard title="Validity and rules" columns={4}>
              <DetailItem label="Valid from">{formatDate(viewList.validFrom)}</DetailItem>
              <DetailItem label="Valid to">{viewList.validTo ? formatDate(viewList.validTo) : 'No end date'}</DetailItem>
              <DetailItem label="Priority">{viewList.priority}</DetailItem>
              <DetailItem label="List discount">{Number(viewList.globalDiscountPercent || 0)}%</DetailItem>
              <DetailItem label="Currency">{viewList.currency || 'INR'}</DetailItem>
              <DetailItem label="Applies to">
                {viewList.customerId ? 'One customer' : 'All customers'}
              </DetailItem>
              <DetailItem label="Created">{formatDate(viewList.createdAt)}</DetailItem>
              <DetailItem label="Last updated">{formatDate(viewList.updatedAt)}</DetailItem>
            </DetailCard>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Product</th>
                    <th className={thClass}>Price Type</th>
                    <th className={`${thClass} text-right`}>Base Price</th>
                    <th className={`${thClass} text-right`}>Final Price</th>
                    <th className={`${thClass} text-right`}>Quantity</th>
                    <th className={`${thClass} text-right`}>Extra Discount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {(viewList.items ?? []).length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={6}>
                        This price list has no product prices
                      </td>
                    </tr>
                  ) : (
                    viewList.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{item.product?.name || 'N/A'}</div>
                          <div className="text-gray-500">{item.product?.sku || ''}</div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {PRICE_TYPE_LABELS[item.priceType as PriceType] || item.priceType}
                          {item.priceType !== PriceType.FIXED && item.percentage != null && (
                            <span className="text-gray-500"> ({Number(item.percentage)}%)</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                          {formatCurrency(Number(item.basePrice), viewList.currency)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">
                          {formatCurrency(Number(item.finalPrice), viewList.currency)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                          {formatNumber(Number(item.minimumQuantity ?? 1))}
                          {item.maximumQuantity != null ? ` – ${formatNumber(Number(item.maximumQuantity))}` : '+'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                          {Number(item.additionalDiscountPercent || 0)}%
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <FormActions>
          <button type="button" onClick={() => setIsViewOpen(false)} className={secondaryButtonClass}>
            Close
          </button>
          {viewList && (
            <button
              type="button"
              onClick={() => {
                setIsViewOpen(false);
                openEdit(viewList);
              }}
              className={primaryButtonClass}
            >
              <PencilIcon className="mr-2 h-4 w-4" />
              Edit Price List
            </button>
          )}
        </FormActions>
      </Modal>

      {/* Activate / deactivate confirmation */}
      <ConfirmDialog
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        title={toggleTarget?.activate ? 'Activate Price List' : 'Deactivate Price List'}
        message={
          <>
            <span className="font-medium text-gray-900">{toggleTarget?.priceList.name}</span>{' '}
            {toggleTarget?.activate
              ? 'will start setting prices on new sales orders.'
              : 'will stop setting prices on new sales orders. Existing orders are not changed.'}
          </>
        }
        confirmLabel={toggleTarget?.activate ? 'Activate' : 'Deactivate'}
        pendingLabel="Updating..."
        isPending={setActiveMutation.isPending}
        tone="primary"
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Price List"
        message={
          <>
            <span className="font-medium text-gray-900">{deleteTarget?.name}</span> and its{' '}
            {deleteTarget?.items?.length || 0} product prices will be permanently deleted. This cannot be
            undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};

export default PriceListManagement;
